/**
 * JsSIP over WSS to the FreeSWITCH agent tier, lifted from infra-sbc/agent-console/static/app.js:
 * register → dial the agent's own room while `wantRoom` (campaign room_join_policy: all shift, from
 * Ready, or from offer; AE-10989) → rejoin after 2s if the leg drops while wanted → leave when
 * `wantRoom` goes false (Pause/Logout) → reject any inbound INVITE (nothing should call the agent in this model).
 * Call control (hold/mute/transfer) is NOT done over SIP; it goes desktop → BFF → sidecar ESL.
 */
import { useEffect, useRef } from 'react';
import JsSIP from 'jssip';
import type { RTCSession } from 'jssip/lib/RTCSession';
import type { Webphone } from '../types';

type Status = 'off' | 'registering' | 'registered' | 'in-room' | 'failed';

export function useWebphone(wp: Webphone | null, wantRoom: boolean, onStatus: (s: Status) => void, onLog?: (line: string) => void) {
  const statusCb = useRef(onStatus); statusCb.current = onStatus;
  const logCb = useRef(onLog); logCb.current = onLog;
  const wantRef = useRef(wantRoom); wantRef.current = wantRoom;
  const syncRef = useRef<(() => void) | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!wp) return;
    const log = (l: string) => logCb.current?.(l);
    const audio = audioRef.current ?? document.createElement('audio');
    audio.autoplay = true;
    audioRef.current = audio;
    let ua: JsSIP.UA | null = null;
    let session: RTCSession | null = null;
    let stopped = false;
    let rejoinTimer: ReturnType<typeof setTimeout> | null = null;

    const hookPc = (pc: RTCPeerConnection | undefined) => {
      if (!pc || (pc as unknown as { __hooked?: boolean }).__hooked) return;
      (pc as unknown as { __hooked?: boolean }).__hooked = true;
      pc.addEventListener('track', (ev) => {
        if (ev.track.kind !== 'audio') return;
        audio.srcObject = ev.streams[0] ?? new MediaStream([ev.track]);
        audio.play().catch((e: Error) => log(`audio playback blocked (${e.name})`));
      });
    };

    const joinRoom = () => {
      if (stopped || !wantRef.current || !ua || !ua.isRegistered() || session) return;
      log(`joining room ${wp.room}`);
      try {
        session = ua.call(wp.room, { ...(wp.ice_servers?.length ? { pcConfig: { iceServers: wp.ice_servers } } : {}), mediaConstraints: { audio: true, video: false }, rtcOfferConstraints: { offerToReceiveAudio: true, offerToReceiveVideo: false } });
      } catch (e) {
        log(`ua.call threw: ${(e as Error).message}`);
        session = null;
      }
    };

    const attach = (rtc: RTCSession) => {
      hookPc(rtc.connection);
      rtc.on('peerconnection', (e: { peerconnection: RTCPeerConnection }) => hookPc(e.peerconnection));
      rtc.on('confirmed', () => statusCb.current('in-room'));
      rtc.on('accepted', () => statusCb.current('in-room'));
      const gone = (e: { originator: string; cause?: string }) => {
        log(`room leg ended (${e.originator}${e.cause ? `, ${e.cause}` : ''})`);
        session = null;
        audio.srcObject = null;
        statusCb.current(ua?.isRegistered() ? 'registered' : 'failed');
        if (!stopped && wantRef.current) rejoinTimer = setTimeout(joinRoom, 2000);
      };
      rtc.on('ended', gone);
      rtc.on('failed', gone);
    };

    // Join or leave to match the policy; the SIP registration stays up either way.
    syncRef.current = () => {
      if (wantRef.current) { joinRoom(); return; }
      if (rejoinTimer) { clearTimeout(rejoinTimer); rejoinTimer = null; }
      try { session?.terminate(); } catch { /* ignore */ }
    };

    const socket = new JsSIP.WebSocketInterface(wp.wss_url);
    ua = new JsSIP.UA({ sockets: [socket], uri: `sip:${wp.sip_user}@${wp.sip_domain}`, password: wp.sip_password, register: true, session_timers: false });
    ua.on('registered', () => { statusCb.current('registered'); joinRoom(); });
    ua.on('unregistered', () => statusCb.current('failed'));
    ua.on('registrationFailed', (e: { cause?: string }) => { log(`registration failed: ${e.cause ?? ''}`); statusCb.current('failed'); });
    ua.on('disconnected', () => statusCb.current('failed'));
    ua.on('newRTCSession', (e: { originator: string; session: RTCSession }) => {
      if (e.originator === 'remote') { e.session.terminate({ status_code: 486, reason_phrase: 'Busy Here' }); return; }
      session = e.session;
      attach(session);
    });
    statusCb.current('registering');
    ua.start();

    return () => {
      stopped = true;
      syncRef.current = null;
      if (rejoinTimer) clearTimeout(rejoinTimer);
      try { session?.terminate(); } catch { /* ignore */ }
      try { ua?.stop(); } catch { /* ignore */ }
      statusCb.current('off');
    };
  }, [wp?.wss_url, wp?.sip_user, wp?.sip_password, wp?.sip_domain, wp?.room]);

  useEffect(() => { syncRef.current?.(); }, [wantRoom]);
}
